import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { queueSubmission, isOnline } from "@/lib/offlineQueue";
import KioskBanner from "@/components/kiosk/KioskBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LogoMark } from "@/components/brand/Logo";
import { CheckCircle2, Sparkles, MessageCircle, MonitorSmartphone, ArrowRight, CloudOff } from "lucide-react";

const DEPARTMENTS = ["Gas Station", "Beach Bar", "Admin", "Office"];

const blankForm = () => ({
  full_name: "",
  whatsapp_number: "",
  department: "",
  q_most_important: "",
  q_apply_this_week: "",
  q_do_differently: "",
  q_questions_thoughts: "",
});

const ReflectionPage = () => {
  const { workshopId, sessionNumber } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const kiosk = searchParams.get("kiosk") === "1";

  const [workshop, setWorkshop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // {whatsapp_status, queued, name}
  const [error, setError] = useState("");
  const [form, setForm] = useState(blankForm());

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get(`/public/workshop-info/${workshopId}?session=${sessionNumber}`)
      .then((r) => setWorkshop(r.data))
      .catch(() => setError("Session link is invalid or expired."))
      .finally(() => setLoading(false));
  }, [workshopId, sessionNumber]);

  // Auto-return to a fresh form after success in kiosk mode.
  useEffect(() => {
    if (!submitted || !kiosk) return;
    const t = setTimeout(() => {
      setSubmitted(null);
      setForm(blankForm());
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 5000);
    return () => clearTimeout(t);
  }, [submitted, kiosk]);

  const enterKiosk = () => {
    const sp = new URLSearchParams(searchParams);
    sp.set("kiosk", "1");
    setSearchParams(sp, { replace: true });
  };

  const exitKiosk = () => {
    const sp = new URLSearchParams(searchParams);
    sp.delete("kiosk");
    setSearchParams(sp, { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.full_name || !form.whatsapp_number || !form.department) {
      setError("Please complete name, WhatsApp number, and department.");
      return;
    }
    setSubmitting(true);
    const payload = {
      workshop_id: workshopId,
      session_number: Number(sessionNumber),
      organisation: workshop?.client_name || "",
      ...form,
    };

    // If we know we're offline, queue immediately and skip the network call.
    if (!isOnline()) {
      queueSubmission("/public/reflection", payload, {
        type: "reflection",
        name: form.full_name,
        whatsapp: form.whatsapp_number,
      });
      setSubmitted({ queued: true, name: form.full_name });
      setSubmitting(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    try {
      const res = await api.post("/public/reflection", payload);
      setSubmitted({
        whatsapp_status: res.data.whatsapp_status,
        is_first_time: res.data.is_first_time_participant,
        name: form.full_name,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      // Network error → queue locally; validation/server error → show.
      const status = err?.response?.status;
      if (!status || status >= 500) {
        queueSubmission("/public/reflection", payload, {
          type: "reflection",
          name: form.full_name,
          whatsapp: form.whatsapp_number,
        });
        setSubmitted({ queued: true, name: form.full_name });
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        setError(err?.response?.data?.detail || "Submission failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6B5944]">Loading…</div>;

  if (error && !workshop) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#FDFBF5]">
        <div className="max-w-md text-center">
          <h1 className="font-display text-2xl text-[#A23E3E]">Link not valid</h1>
          <p className="text-[#6B5944] mt-2">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FDFBF5]" data-testid="reflection-success">
        {kiosk && <KioskBanner label="Kiosk Mode · Reflection" />}
        <div className="flex items-center justify-center p-6 min-h-[80vh]">
          <div className="max-w-xl text-center ccs-fade-up">
            <div className="w-16 h-16 rounded-2xl bg-[#6B8E5A]/15 flex items-center justify-center mx-auto mb-6">
              {submitted.queued ? (
                <CloudOff className="w-8 h-8 text-[#C28A2E]" strokeWidth={1.7} />
              ) : (
                <CheckCircle2 className="w-8 h-8 text-[#6B8E5A]" strokeWidth={1.7} />
              )}
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#C28A2E] font-bold">
              {submitted.queued ? "Saved offline" : "Thank you"}
            </div>
            <h1 className="font-display text-4xl font-semibold tracking-tight text-[#2A1810] mt-3">
              {submitted.name ? `Thanks, ${submitted.name.split(" ")[0]}!` : "Your reflection has been received"}
            </h1>
            {submitted.queued ? (
              <p className="text-[#5D3A1F] mt-4 leading-relaxed">
                We're offline — your reflection has been saved on this device and will sync automatically
                (along with your WhatsApp recap) as soon as the connection returns.
              </p>
            ) : submitted.whatsapp_status === "sent" ? (
              <div className="mt-4 space-y-2">
                {submitted.is_first_time && (
                  <p className="text-[#5D3A1F] leading-relaxed text-sm bg-[#C28A2E]/10 border border-[#C28A2E]/30 rounded-lg px-3 py-2 inline-flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#C28A2E]" />
                    Welcome to The CCS Approach! A welcome message has been sent to your WhatsApp.
                  </p>
                )}
                <p className="text-[#5D3A1F] leading-relaxed flex items-center justify-center gap-2">
                  <MessageCircle className="w-4 h-4 text-[#6B8E5A]" />
                  Your reflection summary has been sent to your WhatsApp.
                </p>
              </div>
            ) : (
              <p className="text-[#6B5944] mt-4 leading-relaxed">
                Your reflection has been saved. Joylance may follow up with you directly.
              </p>
            )}

            {kiosk ? (
              <div className="mt-8 space-y-3">
                <p className="text-xs text-[#6B5944]">Auto-clearing for the next participant in a few seconds…</p>
                <Button
                  onClick={() => { setSubmitted(null); setForm(blankForm()); }}
                  data-testid="kiosk-next-participant-btn"
                  className="bg-[#5D3A1F] hover:bg-[#4A2E18] text-white rounded-xl px-6"
                >
                  Next participant <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            ) : (
              <p className="text-sm text-[#6B5944] mt-8 italic">
                "Let everything you do be done in love." — 1 Corinthians 16:14
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF5]" data-testid="public-reflection-page">
      {kiosk && <KioskBanner label="Kiosk Mode · Reflection" />}

      <header className="border-b border-[#EBE3D2] bg-[#FDFBF5]/85 backdrop-blur-md sticky top-0 z-30" style={kiosk ? { top: 44 } : undefined}>
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <LogoMark size={40} />
          <div className="flex-1">
            <div className="font-display text-base font-semibold text-[#2A1810]">The CCS Approach</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B5944] font-medium">Session Reflection</div>
          </div>
          {!kiosk && (
            <Button
              variant="outline"
              size="sm"
              onClick={enterKiosk}
              className="hidden sm:inline-flex border-[#C28A2E]/40 text-[#5D3A1F] hover:bg-[#C28A2E]/10"
              data-testid="enter-kiosk-btn"
            >
              <MonitorSmartphone className="w-3.5 h-3.5 mr-1.5" /> Use this device for many participants
            </Button>
          )}
          {kiosk && (
            <Button
              variant="ghost"
              size="sm"
              onClick={exitKiosk}
              className="text-[#6B5944]"
              data-testid="exit-kiosk-btn"
            >
              Exit kiosk
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8 lg:py-12">
        <div className="ccs-card ccs-grain p-6 lg:p-8 mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#5D3A1F]/8 text-[#5D3A1F] text-xs font-medium">
            <Sparkles className="w-3 h-3" /> Session {sessionNumber} Reflection
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight text-[#2A1810] mt-3 leading-tight">
            What did today teach you?
          </h1>
          {workshop && (
            <p className="text-sm text-[#6B5944] mt-2">
              <strong>{workshop.name}</strong>
              {workshop.client_name && <> · {workshop.client_name}</>}
              {workshop.date && <> · {workshop.date}</>}
            </p>
          )}
          <p className="text-sm text-[#6B5944] mt-3 leading-relaxed">
            Your reflection helps you remember, helps the team grow, and helps us tailor each session.
            A WhatsApp summary of your responses will be sent to the number you provide
            {kiosk && " — it will arrive whenever you next have data or WiFi"}.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="reflection-form">
          <div className="ccs-card p-5 lg:p-6 space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">WhatsApp Number *</Label>
              <Input
                data-testid="r-whatsapp-input"
                value={form.whatsapp_number}
                onChange={(e) => setF("whatsapp_number", e.target.value)}
                placeholder="868-123-4567 or +18681234567"
                className="h-11 rounded-lg bg-white border-[#EBE3D2]"
                inputMode="tel"
                autoComplete="off"
              />
              <div className="text-xs text-[#6B5944]">Your reflection summary will be sent here.</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Full Name *</Label>
                <Input data-testid="r-name-input" autoComplete="off" value={form.full_name} onChange={(e) => setF("full_name", e.target.value)} className="h-11 rounded-lg bg-white border-[#EBE3D2]" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Department *</Label>
                <Select value={form.department} onValueChange={(v) => setF("department", v)}>
                  <SelectTrigger data-testid="r-dept-select" className="h-11 rounded-lg bg-white border-[#EBE3D2]">
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPARTMENTS.map((d) => (<SelectItem key={d} value={d}>{d}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-xs text-[#6B5944] pt-1">
              <div>
                <span className="uppercase tracking-wider font-bold text-[#C28A2E]">Session</span>
                <div className="font-medium text-[#2A1810] mt-0.5">#{sessionNumber}</div>
              </div>
              <div>
                <span className="uppercase tracking-wider font-bold text-[#C28A2E]">Organisation</span>
                <div className="font-medium text-[#2A1810] mt-0.5">{workshop?.client_name || "—"}</div>
              </div>
            </div>
          </div>

          {(workshop?.reflection_questions || [
            "What is the most important thing I learned today?",
            "How will I apply this in my role this week?",
            "One thing I will do differently starting now",
            "Any questions or thoughts I still have",
          ]).map((label, i) => {
            const keys = ["q_most_important", "q_apply_this_week", "q_do_differently", "q_questions_thoughts"];
            const placeholders = [
              "The one insight you don't want to forget…",
              "Specific actions, conversations, or shifts you'll make…",
              "A new habit, a different response, a fresh approach…",
              "What you'd love to explore further with Joylance…",
            ];
            const key = keys[i];
            return (
              <div key={key} className="ccs-card p-5 lg:p-6 space-y-2">
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-[#C28A2E] text-white font-bold text-sm flex items-center justify-center shrink-0 mt-0.5">{i + 1}</div>
                  <Label className="text-base font-medium text-[#2A1810] leading-snug">{label}</Label>
                </div>
                <Textarea
                  data-testid={`r-${key}`}
                  rows={4}
                  value={form[key]}
                  onChange={(e) => setF(key, e.target.value)}
                  placeholder={placeholders[i]}
                  className="rounded-lg bg-white border-[#EBE3D2]"
                />
              </div>
            );
          })}

          {error && <div className="text-sm text-[#A23E3E] bg-[#A23E3E]/10 rounded-lg p-3" data-testid="reflection-error">{error}</div>}

          <Button
            type="submit" disabled={submitting} data-testid="r-submit-btn"
            className="w-full h-12 rounded-xl bg-[#5D3A1F] hover:bg-[#4A2E18] text-white font-medium text-base hover:-translate-y-[1px] hover:shadow-lg transition-all"
          >
            {submitting ? "Sending…" : (kiosk ? "Submit & Pass to Next Participant" : "Submit Reflection & Send WhatsApp Recap")}
          </Button>
          {!kiosk && (
            <p className="text-xs text-[#6B5944] text-center italic">
              "Let everything you do be done in love." — 1 Corinthians 16:14
            </p>
          )}
        </form>
      </main>
    </div>
  );
};

export default ReflectionPage;
